// RumbleSiegerDisplayPage.jsx — Festive Winner Screen für Rumble Belt Mode
// Zweiter Bildschirm / Beamer — gesteuert via localStorage + BroadcastChannel
import React, { useState, useEffect, useCallback } from 'react';
import './RumbleSiegerDisplayPage.css';

const LS_KEY_STATE = 'tda-rumble-sieger-state';
const LS_KEY_BG    = 'tda-rumble-winner-bg';
const CHANNEL      = 'tda-rumble-sieger';

const BG_OPTIONS = [
  { id: 'kickbox', src: '/images/rumble-bg-kickbox.webp', label: 'Kickbox 🥊' },
  { id: 'karate',  src: '/images/rumble-bg-karate.webp',  label: 'Kampfkünste 🥋' },
];

export default function RumbleSiegerDisplayPage() {
  const [winnerData,   setWinnerData]   = useState(null);
  const [animKey,      setAnimKey]      = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bgId,         setBgId]         = useState(
    () => localStorage.getItem(LS_KEY_BG) || BG_OPTIONS[0].id
  );

  const currentBg = BG_OPTIONS.find(b => b.id === bgId) || BG_OPTIONS[0];

  // Overflow hidden
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow             = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow             = prev.bodyOverflow;
    };
  }, []);

  // Auto-Vollbild beim Laden
  useEffect(() => {
    const timer = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fullscreen-Status tracken
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Chat-Widget und externe Elemente verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT', 'STYLE', 'LINK', 'META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  // Sieger-Daten empfangen (localStorage + BroadcastChannel)
  useEffect(() => {
    const applyState = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed?.type === 'show') {
          setWinnerData(parsed);
          setAnimKey(k => k + 1);
        } else {
          setWinnerData(null);
        }
      } catch (e) {}
    };

    // Sofort lesen beim Öffnen
    applyState(localStorage.getItem(LS_KEY_STATE));

    const onStorage = (e) => {
      if (e.key === LS_KEY_STATE) applyState(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => applyState(e.data);
    } catch (e) {}

    return () => {
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }
  }, []);

  const cycleBg = useCallback((dir) => {
    setBgId(prev => {
      const idx     = BG_OPTIONS.findIndex(b => b.id === prev);
      const next    = (idx + dir + BG_OPTIONS.length) % BG_OPTIONS.length;
      const newId   = BG_OPTIONS[next].id;
      localStorage.setItem(LS_KEY_BG, newId);
      return newId;
    });
  }, []);

  const selectBg = useCallback((id) => {
    setBgId(id);
    localStorage.setItem(LS_KEY_BG, id);
  }, []);

  // Setup-Screen solange kein Vollbild
  if (!isFullscreen) {
    return (
      <SetupOverlay
        bgOptions={BG_OPTIONS}
        selectedBg={bgId}
        onSelectBg={selectBg}
        onFullscreen={toggleFullscreen}
        currentBgSrc={currentBg.src}
      />
    );
  }

  return (
    <div
      className="rsd-page"
      style={{ backgroundImage: `url(${currentBg.src})` }}
      onClick={toggleFullscreen}
    >
      {winnerData
        ? <WinnerCard key={animKey} data={winnerData} />
        : <StandbyCard />
      }

      {/* Hintergrundbild-Wechsler */}
      <div className="rsd-bg-switcher" onClick={e => e.stopPropagation()}>
        {BG_OPTIONS.map(bg => (
          <button
            key={bg.id}
            className={`rsd-bg-dot${bgId === bg.id ? ' active' : ''}`}
            title={bg.label}
            onClick={() => selectBg(bg.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Setup Overlay ─────────────────────────────────────────────────────
function SetupOverlay({ bgOptions, selectedBg, onSelectBg, onFullscreen, currentBgSrc }) {
  return (
    <div
      className="rsd-setup"
      style={{ backgroundImage: `url(${currentBgSrc})` }}
    >
      <div className="rsd-setup-card">
        <div className="rsd-setup-emoji">🏆</div>
        <div className="rsd-setup-title">Rumble Siegerbildschirm</div>
        <div className="rsd-setup-sub">Hintergrundbild wählen, dann Vollbild starten</div>

        <div className="rsd-bg-grid">
          {bgOptions.map(bg => (
            <div
              key={bg.id}
              className={`rsd-bg-option${selectedBg === bg.id ? ' active' : ''}`}
              style={{ backgroundImage: `url(${bg.src})` }}
              onClick={() => onSelectBg(bg.id)}
              title={bg.label}
            >
              <div className="rsd-bg-option-label">{bg.label}</div>
            </div>
          ))}
        </div>

        <button className="rsd-fullscreen-btn" onClick={onFullscreen}>
          ⛶ Vollbild starten
        </button>
      </div>
    </div>
  );
}

// ── Standby ───────────────────────────────────────────────────────────
function StandbyCard() {
  return (
    <div className="rsd-standby">
      <div className="rsd-standby-icon">🥋</div>
      <div className="rsd-standby-txt">Bereit für nächsten Sieger</div>
    </div>
  );
}

// ── Winner Card ───────────────────────────────────────────────────────
function WinnerCard({ data }) {
  const { vorname, nachname, verein, belts = 3, divisionCode, winReason } = data;

  return (
    <div className="rsd-winner-wrap">
      <div className="rsd-winner-card">
        {winReason && <div className="rsd-reason">{winReason}</div>}

        <div className="rsd-name">{vorname} {nachname}</div>

        {verein && <div className="rsd-club">{verein}</div>}

        <div className="rsd-belts">
          {Array.from({ length: belts }).map((_, i) => (
            <span
              key={i}
              className="rsd-belt"
              style={{ animationDelay: `${0.1 + i * 0.12}s` }}
            >
              🥋
            </span>
          ))}
        </div>

        {divisionCode && (
          <div className="rsd-division">{divisionCode}</div>
        )}
      </div>
    </div>
  );
}
